/**
 * Monotone cubic interpolation (Fritsch–Carlson, the PCHIP scheme), sampled into
 * points a `linear` trace can draw as a smooth curve.
 *
 * Plotly's `line.shape: 'spline'` is a Catmull-Rom curve, which bulges past the
 * data wherever the line leaves a plateau — an accuracy curve flat at 100 % was
 * drawn above 100 % (Bug 2764). With these tangents every segment stays between
 * its two end values: a plateau stays flat and no sample passes a data point.
 */

import { toFiniteNumber } from './number';

/** Samples per segment between two data points (the end point is added once). */
const SAMPLES_PER_SEGMENT = 16;

export interface SampledCurve {
  x: (number | null)[];
  y: (number | null)[];
}

const sign = (v: number) => (v > 0 ? 1 : v < 0 ? -1 : 0);

/** Tangent at each point of one run with no gaps (`xs` strictly increasing). */
function tangents(xs: number[], ys: number[]): number[] {
  const n = xs.length;
  const h = xs.slice(1).map((x, k) => x - xs[k]);
  const d = h.map((hk, k) => (ys[k + 1] - ys[k]) / hk);
  if (n === 2) return [d[0], d[0]];
  const m = new Array<number>(n);
  for (let k = 1; k < n - 1; k++) {
    if (d[k - 1] * d[k] <= 0) {
      m[k] = 0;
    } else {
      // Weighted harmonic mean of the neighbouring slopes.
      const w1 = 2 * h[k] + h[k - 1];
      const w2 = h[k] + 2 * h[k - 1];
      m[k] = (w1 + w2) / (w1 / d[k - 1] + w2 / d[k]);
    }
  }
  // One-sided three-point end slopes, limited so the end segments stay monotone.
  const end = (h0: number, h1: number, d0: number, d1: number) => {
    const t = ((2 * h0 + h1) * d0 - h0 * d1) / (h0 + h1);
    if (sign(t) !== sign(d0)) return 0;
    if (sign(d0) !== sign(d1) && Math.abs(t) > Math.abs(3 * d0)) return 3 * d0;
    return t;
  };
  m[0] = end(h[0], h[1], d[0], d[1]);
  m[n - 1] = end(h[n - 2], h[n - 3], d[n - 2], d[n - 3]);
  return m;
}

function sampleRun(xs: number[], ys: number[], out: SampledCurve): void {
  if (xs.length === 1) {
    out.x.push(xs[0]);
    out.y.push(ys[0]);
    return;
  }
  const m = tangents(xs, ys);
  for (let k = 0; k < xs.length - 1; k++) {
    const h = xs[k + 1] - xs[k];
    const dy = ys[k + 1] - ys[k];
    for (let j = 0; j < SAMPLES_PER_SEGMENT; j++) {
      const t = j / SAMPLES_PER_SEGMENT;
      const t2 = t * t;
      const t3 = t2 * t;
      // Written as y0 + Δy·h01 + h·(…) so a flat segment (Δy = 0, m = 0) is exact.
      const h01 = -2 * t3 + 3 * t2;
      const h10 = t3 - 2 * t2 + t;
      const h11 = t3 - t2;
      out.x.push(j === 0 ? xs[k] : xs[k] + t * h);
      out.y.push(j === 0 ? ys[k] : ys[k] + dy * h01 + h * (h10 * m[k] + h11 * m[k + 1]));
    }
  }
  out.x.push(xs[xs.length - 1]);
  out.y.push(ys[ys.length - 1]);
}

/**
 * Sample the monotone curve through `(x, y)`. A missing `y` stays a gap (a `null`
 * pair between the runs on either side). Returns null when `x` is not a strictly
 * increasing run of numbers — there is no curve to draw in X order then, and the
 * caller falls back to straight segments.
 */
export function monotoneCurve(x: readonly unknown[], y: readonly unknown[]): SampledCurve | null {
  const xs: number[] = [];
  for (const v of x) {
    if (typeof v !== 'number' || !Number.isFinite(v)) return null;
    if (xs.length > 0 && v <= xs[xs.length - 1]) return null;
    xs.push(v);
  }
  const out: SampledCurve = { x: [], y: [] };
  let runX: number[] = [];
  let runY: number[] = [];
  const flush = () => {
    if (runX.length === 0) return;
    if (out.x.length > 0) {
      out.x.push(null);
      out.y.push(null);
    }
    sampleRun(runX, runY, out);
    runX = [];
    runY = [];
  };
  xs.forEach((xv, i) => {
    const yv = toFiniteNumber(y[i]);
    if (yv === null) {
      flush();
    } else {
      runX.push(xv);
      runY.push(yv);
    }
  });
  flush();
  return out;
}
